import React from 'react';

import camelize from 'lodash/camelCase';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { parseVideoIdInput } from '~/shared/utils/assetHelper';
import goToAnchor from '~/shared/utils/goToAnchor';
import useCustomerIsGendered from '~/shared/utils/useCustomerIsGendered';
import usePromoTracking from '~/shared/utils/usePromoTracking';
import { Router } from '~/techstyle-shared/next-routes';
import { Gender } from '~/techstyle-shared/react-accounts';
import { ImageAsset } from '~/techstyle-shared/react-assets';
import { desktop, Video } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

const DynamicFormattedMessage = FormattedMessage;

const Card = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: ${({ $mobileCardLayout }) => $mobileCardLayout};
  padding: 12px 9px;
  background-color: ${({ theme }) => theme.colors.white};
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
  transition: all 200ms ease-out;

  ${({ $isClickable }) =>
    $isClickable &&
    `
    &:hover {
      transform: translate3d(0, -10px, 0);
      box-shadow: 0px 3px 15px rgba(0,0,0,0.2);
    }
  `}

  ${desktop`
    max-width: 315px;
    flex-direction: column;
    padding: unset;
  `}
  ${({ theme }) => theme.marketingCard.variants.VIPHomepage.card}
`;

const CardTitle = styled.div`
  ${({ theme: { typography } }) => typography.values.heading2small}
  font-family: ${({ theme: { fontStacks } }) =>
    fontStacks.condensedVipHomepageFamily};
  font-weight: 800;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  margin-bottom: ${({ theme: { sizes } }) => sizes.md}px;

  ${desktop`
    line-height: 42px;
    margin-bottom: 12px;
  `}
  ${({ theme }) => theme.marketingCard.variants.VIPHomepage.cardTitle}
`;

const CardBody = styled.div`
  min-height: 150px;
  display: flex;
  flex-direction: column;
  padding: 21px 0 21px 12px;

  ${desktop`
    padding: 12px 4px 4px 25px;
  `}

  text-align: start;
  text-transform: capitalize;
  ${({ theme }) => theme.marketingCard.variants.VIPHomepage.cardbody}
`;

const CardImage = styled(ImageAsset)`
  max-width: ${({ mobileCardLayout }) =>
    mobileCardLayout === 'column' ? '100%' : '160px'};
  pointer-events: none;
  width: 100%;

  ${desktop`
    max-width: unset;
  `}
`;

const CardVideo = styled(Video)`
  max-width: ${({ mobileCardLayout }) =>
    mobileCardLayout === 'column' ? '100%' : '160px'};
  width: 100%;

  ${desktop`
    max-width: unset;
  `}
`;

const VideoWrapperStyle = css`
  width: 100%;
`;

const CardSubtitle = styled.div`
  ${({ theme: { typography } }) => typography.values.heading4small}
  font-family: ${({ theme: { fontStacks } }) => fontStacks.vipHomepageFamily};
  font-style: normal;
  font-weight: 500;
  letter-spacing: -0.02em;
`;

const VIPHomepageMarketingCard = ({ asset, mobileCardLayout, refs }) => {
  const gender = useCustomerIsGendered();
  const isMale = gender === Gender.Male;
  const { triggerPromoTracking } = usePromoTracking();
  const genderIndicator = isMale ? 'Mens' : 'Womens';

  const {
    link = '',
    title = '',
    subtitle = '',
    anchorTo = null,
  } = asset?.options?.customVars;
  // check for a video id
  const { videoId, videoAspectRatio } = parseVideoIdInput({ asset });

  const onClick = () => {
    if (!anchorTo && !link) {
      return;
    }

    triggerPromoTracking(asset);
    if (anchorTo) {
      goToAnchor(refs, anchorTo);
    } else if (link) {
      Router.push(link);
    }
  };

  return (
    <Card
      onClick={onClick}
      $isClickable={!!anchorTo || !!link}
      $mobileCardLayout={mobileCardLayout}
    >
      {videoId ? (
        <CardVideo
          aspectRatio={videoAspectRatio}
          url={Video.getUrlFromVideoId(videoId)}
          wrapperStyle={VideoWrapperStyle}
          mobileCardLayout={mobileCardLayout}
        />
      ) : (
        <CardImage asset={asset} mobileCardLayout={mobileCardLayout} />
      )}
      <CardBody>
        {title && title.length > 0 && (
          <CardTitle>
            <DynamicFormattedMessage
              id={`BMIG_Vip_Homepage_${genderIndicator}.asset_card_title-${camelize(
                title
              )}`}
              defaultMessage={title}
            />
          </CardTitle>
        )}
        {subtitle && subtitle.length > 0 && (
          <CardSubtitle>
            <DynamicFormattedMessage
              id={`BMIG_Vip_Homepage_${genderIndicator}.asset_card_subtitle-${camelize(
                title
              )}`}
              defaultMessage={subtitle}
            />
          </CardSubtitle>
        )}
      </CardBody>
    </Card>
  );
};

export default VIPHomepageMarketingCard;

VIPHomepageMarketingCard.propTypes = {
  asset: PropTypes.object,
  mobileCardLayout: PropTypes.string,
  refs: PropTypes.object,
};
